import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EditorComponent } from '@progress/kendo-angular-editor';
import { ToolBarButtonComponent, ToolBarComponent } from '@progress/kendo-angular-toolbar';
import { handleResizeAltIcon, handleResizeIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { combineLatest, filter, fromEvent, of, Subscription, takeWhile } from 'rxjs';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { collapsePath, expandPath } from '../kendo-icons/kendo-icons.constants';
import { FireflyLocalizationService } from '../utils/localization/firefly-localization.service';

// TODO: add input property to indicate the type of toolbar
@Component({
  selector: 'f-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FireflyRichTextEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  toolbarWidth = 100;
  pasteCleanupSettings = {
    convertMsLists: true,
    removeHtmlComments: true,
    stripTags: ['script', 'noscript', 'applet', 'embed', 'object', 'param', 'style', 'img'],
    removeAttributes: [''],
    removeMsClasses: true,
    removeMsStyles: true,
    removeInvalidHTML: true
  };
  collapseIcon = handleResizeIcon;
  expandIcon = handleResizeAltIcon;
  initNotes = '';
  newNotes = '';

  @Input() placeholder: string = '';
  @Input() set value(notes: string) {
    this.newNotes = notes;
    this.initNotes = notes;
  }
  @Input() toolbarOverflow: boolean = true;
  @Input() limit: number = 70000;
  @Input() enableResizing = true;
  @Input() valid = true;
  @Input() showAutoSaveErrorMessage: boolean | null = false;
  @Input() showAutoSaveSuccessMessage: boolean | null = false;
  @Input() draftFailureMessage = '';
  @Output() valueChange = new EventEmitter<string>();
  @Output() resizeNotes = new EventEmitter<boolean>();
  @Output() characterLimitValidation = new EventEmitter<boolean>();

  @HostBinding('class.k-expanded-editor') isExpanded = false;
  @HostBinding('class.k-footer-visible-expanded-mode') get isFooterVisibleInExpandedMode() {
    return (
      this.isExpanded &&
      (this.showAutoSaveErrorMessage || this.showAutoSaveSuccessMessage || !this.isCharacterLimitValid)
    );
  }
  @ViewChild(ToolBarComponent) private toolbar!: ToolBarComponent;
  @ViewChild(EditorComponent) private editor!: EditorComponent;

  @HostListener('window:beforeunload')
  triggerConfirmationLeavePopup(e: BeforeUnloadEvent): void {
    if (this.newNotes !== this.initNotes) e.preventDefault();
  }

  editorFontSize = 'Size';
  editorPlaceholder = 'Add notes';
  editorBold = 'Bold';
  editorItalic = 'Italic';
  editorUnderline = 'Underline';
  editorColor = 'Color';
  editorUnorderedList = 'Insert unordered list';
  editorOrderedList = 'Insert ordered list';
  editorCleanFormatting = 'Clean Formatting';
  editorCharactersLimit = 'Characters limit exceeded';
  editorCollapseTitle = 'Collapse editor';
  editorExpandTitle = 'Expand editor';
  editorMoreTools = 'More tools';
  editorClearValue = 'Clear value';
  localizationIsReady = false;
  draftSuccessMessage = 'Draft saved just now';

  private _subs = new Subscription();
  private store = inject(Store, { optional: true });
  private destroyRef = inject(DestroyRef);
  private removeEventListener?: () => void;
  lastY = 0;

  changedScrollEnabled: Signal<boolean | undefined>;

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    @Optional() private localizationService: FireflyLocalizationService
  ) {
    this.changedScrollEnabled = toSignal(
      this.store?.select(getFeatureToggle('mobile-scroll-behavior-CPD-2028')) || of(false)
    );
  }

  get resizeIcon(): SVGIcon {
    return this.isExpanded ? this.collapseIcon : this.expandIcon;
  }

  get resizeTitle(): string {
    return this.isExpanded ? this.editorCollapseTitle : this.editorExpandTitle;
  }

  ngOnInit() {
    this.expandIcon.content = expandPath;
    this.collapseIcon.content = collapsePath;
    // workaround for on push change detection with kendo-editor issue: https://github.com/telerik/kendo-angular/issues/3998
    this._subs.add(this.zone.onMicrotaskEmpty.subscribe(() => this.cdr.markForCheck()));

    if (!this.localizationService) {
      this.localizationIsReady = true;
      return;
    }

    this._subs.add(
      combineLatest([
        this.localizationService?.localize('editorFontSize', {}),
        this.localizationService?.localize('editorPlaceholder', {}),
        this.localizationService?.localize('editorBold', {}),
        this.localizationService?.localize('editorItalic', {}),
        this.localizationService?.localize('editorUnderline', {}),
        this.localizationService?.localize('editorColor', {}),
        this.localizationService?.localize('editorUnorderedList', {}),
        this.localizationService?.localize('editorOrderedList', {}),
        this.localizationService?.localize('editorCleanFormatting', {}),
        this.localizationService?.localize('editorCharactersLimit', {}),
        this.localizationService?.localize('editorCollapseTitle', {}),
        this.localizationService?.localize('editorExpandTitle', {}),
        this.localizationService?.localize('editorMoreTools', {}),
        this.localizationService?.localize('editorClearValue', {}),
        this.localizationService?.localize('draftSuccessMessage', {})
      ]).subscribe(
        ([
          editorFontSize,
          editorPlaceholder,
          editorBold,
          editorItalic,
          editorUnderline,
          editorColor,
          editorUnorderedList,
          editorOrderedList,
          editorCleanFormatting,
          editorCharactersLimit,
          editorCollapseTitle,
          editorExpandTitle,
          editorMoreTools,
          editorClearValue,
          draftSuccessMessage
        ]) => {
          this.editorFontSize = editorFontSize;
          this.editorPlaceholder = editorPlaceholder;
          this.editorBold = editorBold;
          this.editorItalic = editorItalic;
          this.editorUnderline = editorUnderline;
          this.editorColor = editorColor;
          this.editorUnorderedList = editorUnorderedList;
          this.editorOrderedList = editorOrderedList;
          this.editorCleanFormatting = editorCleanFormatting;
          this.editorCharactersLimit = editorCharactersLimit;
          this.editorCollapseTitle = editorCollapseTitle;
          this.editorExpandTitle = editorExpandTitle;
          this.editorMoreTools = editorMoreTools;
          this.editorClearValue = editorClearValue;
          this.draftSuccessMessage = draftSuccessMessage;
          this.localizationIsReady = true;
          this.cdr.detectChanges();
        }
      )
    );
  }

  ngAfterViewInit() {
    const colorPicker = document.querySelector('kendo-colorpicker');
    if (colorPicker)
      this.removeEventListener = this.renderer.listen(colorPicker, 'click', () => {
        const colorPickerClearIcon = document.querySelector('.k-coloreditor-reset');
        if (colorPickerClearIcon) this.renderer.setAttribute(colorPickerClearIcon, 'title', this.editorClearValue);
      });
    if (this.toolbarOverflow) {
      // workaround for proper toolbar size on editor open https://github.com/telerik/kendo-angular/issues/4085
      // call `onResize` once Zone is stable and overflow button is overflowed by `toolbarWidth`,
      // whether it is visible or hidden to make toolbar init it's size properly
      this._subs.add(
        this.zone.onStable
          .pipe(
            // `onResize` could need to be called several times during Zone has pending macrotasks to achieve proper
            // size initialization for toolbar (this case takes place at least in Storybook)
            takeWhile(() => this.zone.hasPendingMacrotasks),
            filter(() => this.isOverflowButtonOverflowed())
          )
          .subscribe(() => this.zone.runTask(this.toolbar.onResize, this.toolbar))
      );
    }

    this.scrollHandler();
  }

  private scrollHandler() {
    if (!this.changedScrollEnabled()) return;

    const targetEl = document.querySelector('.k-editor-content .ProseMirror') as HTMLElement;

    fromEvent(targetEl, 'touchstart')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => (this.lastY = (event as TouchEvent).touches[0].clientY));

    fromEvent(targetEl, 'touchmove', {
      passive: false
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.preventScrollOverflow(event as TouchEvent, targetEl));
  }

  private preventScrollOverflow(event: TouchEvent, targetEl: HTMLElement) {
    const top = event.touches[0].clientY;
    const scrollTop = targetEl.scrollTop;
    const maxScrollTop = targetEl.scrollHeight - targetEl.offsetHeight;
    const direction = this.lastY - top < 0 ? 'up' : 'down';
    if (
      event.cancelable &&
      ((scrollTop <= 0 && direction === 'up') || (scrollTop >= maxScrollTop && direction === 'down'))
    )
      event.preventDefault();

    this.lastY = top;
  }

  isOverflowButtonOverflowed(): boolean {
    const overflowButton = this.toolbar.overflowButton.nativeElement;
    overflowButton.setAttribute('title', this.editorMoreTools);
    // offsetParent right padding could not be taken into account because `onResize` function does not lead to resize
    // but it leads to stack overflow in that case
    return overflowButton.offsetLeft + overflowButton.offsetWidth >= overflowButton.offsetParent.offsetWidth;
  }

  get isCharacterLimitValid(): boolean {
    return (
      this.newNotes
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>').length < this.limit
    );
  }

  onValueChange(value: string) {
    this.newNotes = value;
    this.characterLimitValidation.emit(this.isCharacterLimitValid);
    this.valueChange.emit(value);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab' && event.shiftKey) {
      const isEditorFocused = (event.target as HTMLElement)?.id.includes('k-editor');
      if (isEditorFocused) {
        requestAnimationFrame(() => {
          (this.toolbar.allTools.first as ToolBarButtonComponent).toolbarButtonElement.nativeElement.focus();
        });
      }
    }
  }

  onResizeNotes() {
    const el = document.querySelector('.ca-content');

    this.isExpanded = !this.isExpanded;
    this.resizeNotes.emit(this.isExpanded);

    if (this.isExpanded) {
      el!.scrollTop = 0;
      this.renderer.addClass(el, 'overflow-hidden');
    } else {
      this.renderer.removeClass(el, 'overflow-hidden');
    }

    // workaround to remove the focused state from the first button in the toolbar
    // approach with applying focus on editor leads to unpredictable page movement on virtual keyboard appearance event
    // both calls should be inside `requestAnimationFrame` to avoid movement of the browser toolbar at the bottom of the mobile browser
    requestAnimationFrame(() => {
      this.editor.focus();
      this.editor.blur();
    });
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
    this.removeEventListener?.();
  }
}
